import { isEmpty, isNull } from 'lodash';
import { tNotificationsContext } from './Notification.types.ts';
import { useEffect, useState } from 'react';
import {
  dismissNotification,
  getNotifications,
} from './Notifications.service.ts';
import { tGenericNotification } from 'src/notifications/AgendaNotifications.types';
import { useWorkspaceContext } from '../WorkspaceProvider/Workspace.context.ts';
import { useUserContext } from '../UserProvider/User.context.ts';
import { useSocketsContext } from '../SocketsProvider/Sockets.context.ts';
import { useWapiAnalytics } from 'src/hooks/analytics/useWapiAnalytics.hook';
import { WAPI_EVENTS } from 'src/hooks/analytics/wapiAnalytics.constants';

const useNotifications = (): tNotificationsContext => {
  const [currentNotifications, setCurrentNotifications] = useState<
    tGenericNotification[] | null
  >([]);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const { currentWorkspaceId } = useWorkspaceContext();
  const { currentUserId } = useUserContext();
  const { newNotificationEvent, catchUpEvent } = useSocketsContext();
  const { trackWapiEvent, wapiIntegration } = useWapiAnalytics();

  newNotificationEvent.useSubscription((notification) => {
    setCurrentNotifications((notifications) => {
      if (isNull(notifications)) {
        return [notification];
      }
      return [...notifications, notification];
    });

    const isWapiNotification =
      notification.event?.includes('wapi') ||
      notification.event?.includes('whatsapp');

    if (wapiIntegration && isWapiNotification) {
      trackWapiEvent(WAPI_EVENTS.NOTIFICATION_VIEW, {
        notificationType: notification.event,
      });
    }
  });

  catchUpEvent.useSubscription(() => {
    setRefetchTrigger((prev) => prev + 1);
  });

  useEffect(() => {
    (async () => {
      if (currentWorkspaceId === undefined || currentUserId === undefined) {
        return;
      }

      const notifications = await getNotifications(
        currentWorkspaceId,
        currentUserId,
      );
      if (isEmpty(notifications)) {
        return;
      }
      setCurrentNotifications(notifications);
    })();
  }, [currentWorkspaceId, currentUserId, refetchTrigger]);

  async function closeNotification(notificationId: string) {
    if (isNull(currentNotifications)) {
      return;
    }
    await dismissNotification(notificationId);
    setCurrentNotifications(
      (notifications) =>
        notifications?.filter(
          (notification) => notification.id !== notificationId,
        ) ?? [],
    );
  }

  return {
    currentNotifications,
    setCurrentNotifications,
    closeNotification,
  };
};

export default useNotifications;
